import { useState } from "react";
import Papa from "papaparse";
import Icon from "@mdi/react";
import { mdiClose, mdiFileUploadOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { BulkApproveOrdersReviewCsvApi } from "../../../../../service/repos/order_repo";

type PreviewRow = {
  order_id: string;
  approval_action: string;
  selected_warehouse: string;
  suggested_transport: string;
  warehouse_suggestion_remarks: string;
};

type Summary = {
  total_rows: number;
  approved: { count: number; order_ids: string[] };
  skipped: { count: number; rows: { order_id: string; reason: string }[] };
  failed: { count: number; rows: { order_id: string; reason: string }[] };
};

interface Props {
  open: boolean;
  onClose: () => void;
  onDone?: () => void; // refresh the list after a successful approval
}

// Candidate header names (lowercased) for the order id and the action column,
// mirroring the backend reader.
const ORDER_ID_KEYS = ["order_id", "order id", "orderid"];
const ACTION_KEYS = [
  "approval_action",
  "approval action",
  "approver_remarks",
  "approver remarks",
  "action",
];
const WAREHOUSE_KEYS = [
  "selected warehouse",
  "selected_warehouse",
  "suggested warehouse",
  "warehouse",
];
const TRANSPORT_KEYS = [
  "suggested transport",
  "suggested_transport",
  "suggested transport mode",
  "suggested_transport_mode",
  "mode of transport",
  "transport mode",
];
const REMARKS_KEYS = [
  "warehouse suggestion remarks",
  "warehouse_suggestion_remarks",
  "suggestion remarks",
  "remarks",
];

const firstNonEmpty = (row: Record<string, string>, keys: string[]) => {
  for (const k of keys) {
    const v = (row[k] ?? "").toString().trim();
    if (v) return v;
  }
  return "";
};

const BulkApproveUploadModal = ({ open, onClose, onDone }: Props) => {
  const { showToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);

  const reset = () => {
    setFile(null);
    setRows([]);
    setSummary(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (f?: File | null) => {
    setSummary(null);
    if (!f) return;
    setFile(f);
    Papa.parse<Record<string, string>>(f, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h.replace(/^﻿/, "").trim().toLowerCase(),
      complete: (result) => {
        // An order can appear on many rows (one per line item). Collapse to one
        // row per order_id: approve if ANY row says Approve; warehouse = first
        // non-empty across its rows.
        const map = new Map<string, PreviewRow>();
        for (const r of result.data || []) {
          const order_id = firstNonEmpty(r, ORDER_ID_KEYS);
          if (!order_id) continue;
          const action = firstNonEmpty(r, ACTION_KEYS);
          const warehouse = firstNonEmpty(r, WAREHOUSE_KEYS);
          const transport = firstNonEmpty(r, TRANSPORT_KEYS);
          const remarks = firstNonEmpty(r, REMARKS_KEYS);
          const existing = map.get(order_id);
          if (!existing) {
            map.set(order_id, {
              order_id,
              approval_action: action,
              selected_warehouse: warehouse,
              suggested_transport: transport,
              warehouse_suggestion_remarks: remarks,
            });
          } else {
            if (action.toLowerCase() === "approve")
              existing.approval_action = action;
            else if (!existing.approval_action && action)
              existing.approval_action = action;
            if (!existing.selected_warehouse && warehouse)
              existing.selected_warehouse = warehouse;
            if (!existing.suggested_transport && transport)
              existing.suggested_transport = transport;
            if (!existing.warehouse_suggestion_remarks && remarks)
              existing.warehouse_suggestion_remarks = remarks;
          }
        }
        const parsed = Array.from(map.values());
        setRows(parsed);
        if (!parsed.length) {
          showToast({
            type: ToastType.error,
            text: "No Order IDs found in the file.",
          });
        }
      },
      error: () => {
        showToast({ type: ToastType.error, text: "Could not parse the CSV." });
      },
    });
  };

  const willApproveCount = rows.filter(
    (r) => r.approval_action.toLowerCase() === "approve",
  ).length;

  const handleFinalApprove = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await BulkApproveOrdersReviewCsvApi(form);
      if (res.success) {
        setSummary(res.summary);
        showToast({
          type: ToastType.success,
          text: `Approved ${res.summary?.approved?.count ?? 0} order(s)`,
        });
        onDone?.();
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to process approval file",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Upload failed" });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="text-base font-bold">Bulk Approve Orders</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700">
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="p-4 overflow-auto">
          <p className="text-sm text-gray-600 mb-3">
            In the <span className="font-semibold">approval_action</span> column,
            enter <span className="font-semibold">Approve</span> for the orders
            you want to approve and leave it blank to skip. Optional{" "}
            <span className="font-semibold">Suggested Transport</span> (Surface/
            Air/Train) and <span className="font-semibold">Remarks</span>{" "}
            columns are shown to the warehouse on the outward record. Then
            attach the file and review below before final approval.
          </p>

          <label className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium cursor-pointer hover:bg-gray-50">
            <Icon path={mdiFileUploadOutline} size={0.8} />
            {file ? "Change file" : "Attach CSV"}
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </label>
          {file && (
            <span className="ml-2 text-xs text-gray-500">{file.name}</span>
          )}

          {rows.length > 0 && !summary && (
            <div className="mt-4">
              <div className="text-xs text-gray-600 mb-2">
                {rows.length} order(s) in file · {willApproveCount} marked{" "}
                <span className="font-semibold text-green-700">Approve</span>
              </div>
              <div className="border rounded max-h-72 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Order ID</th>
                      <th className="text-left p-2">Approval Action</th>
                      <th className="text-left p-2">Selected Warehouse</th>
                      <th className="text-left p-2">Suggested Transport</th>
                      <th className="text-left p-2">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const isApprove =
                        r.approval_action.toLowerCase() === "approve";
                      return (
                        <tr key={i} className="border-t">
                          <td className="p-2">{r.order_id}</td>
                          <td className="p-2">
                            <span
                              className={`rounded px-2 py-0.5 ${
                                isApprove
                                  ? "bg-green-100 text-green-700 font-medium"
                                  : "text-gray-400"
                              }`}
                            >
                              {r.approval_action || "— (skip)"}
                            </span>
                          </td>
                          <td className="p-2 text-gray-600">
                            {r.selected_warehouse || "—"}
                          </td>
                          <td className="p-2 text-gray-600">
                            {r.suggested_transport || "—"}
                          </td>
                          <td className="p-2 text-gray-600">
                            {r.warehouse_suggestion_remarks || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {summary && (
            <div className="mt-4 text-sm">
              <div className="font-semibold mb-2">Result</div>
              <div className="flex flex-wrap gap-3 mb-3">
                <span className="rounded bg-gray-100 px-2 py-1">
                  Processed: {summary.total_rows}
                </span>
                <span className="rounded bg-green-100 text-green-700 px-2 py-1">
                  Approved: {summary.approved?.count ?? 0}
                </span>
                <span className="rounded bg-amber-100 text-amber-700 px-2 py-1">
                  Skipped: {summary.skipped?.count ?? 0}
                </span>
                <span className="rounded bg-red-100 text-red-700 px-2 py-1">
                  Failed: {summary.failed?.count ?? 0}
                </span>
              </div>
              {summary.failed?.count > 0 && (
                <div className="border rounded max-h-48 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Order ID</th>
                        <th className="text-left p-2">Failure reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.failed.rows.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{r.order_id}</td>
                          <td className="p-2 text-red-600">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t p-4">
          <button
            onClick={handleClose}
            className="rounded-md border px-4 py-2 text-sm"
          >
            {summary ? "Close" : "Cancel"}
          </button>
          {!summary && (
            <button
              onClick={handleFinalApprove}
              disabled={loading || !file || willApproveCount === 0}
              className="rounded-md bg-myPrimaryColor px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {loading
                ? "Approving…"
                : `Final Approve${
                    willApproveCount ? ` (${willApproveCount})` : ""
                  }`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkApproveUploadModal;
